import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { CheckCircle2, Clock, ShieldCheck, ArrowRight, Truck, Phone, CalendarCheck } from "lucide-react";
import { SITE } from "../config/site";
import { SERVICES, TRUST_BADGES, CITIES, IMAGES } from "../data/content";
import { Reveal, Eyebrow, useSeo, EASE } from "../components/Reveal";
import { UrgentButton } from "../components/UrgentModal";
import { AreaChecker } from "../components/AreaChecker";
import { QuoteEstimator } from "../components/QuoteEstimator";

const HERO_LINES = ["Professional testing", "at your location,", "on your schedule."];

function CityMarquee() {
  const items = [...CITIES.slice(0, 14), SITE.serviceRadius];
  const doubled = [...items, ...items];
  return (
    <div data-testid="city-marquee" className="overflow-hidden border-y border-white/10 bg-slate-900 py-5">
      <div className="animate-marquee flex w-max items-center whitespace-nowrap">
        {doubled.map((c, i) => (
          <span key={i} className="flex items-center text-sm font-semibold tracking-wide text-slate-300">
            <span className="px-6">{c}</span>
            <span className="h-1.5 w-1.5 rounded-full bg-teal-500" />
          </span>
        ))}
      </div>
    </div>
  );
}

export default function Home() {
  useSeo(
    "SwiftTest Solutions LLC | Mobile Drug, DNA & DOT Testing — Eastern Iowa",
    "Mobile and on-site drug, DNA, DOT and workplace testing across Eastern Iowa. We travel to your location on your schedule — 24/7 dispatch available."
  );
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 700], [0, 70]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-white">
        <div
          className="pointer-events-none absolute -left-40 top-10 h-[480px] w-[480px] rounded-full bg-teal-100/70 blur-3xl"
          aria-hidden="true"
        />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 pb-20 pt-32 sm:px-8 lg:grid-cols-12 lg:pt-40">
          <div className="lg:col-span-7">
            <motion.span
              data-testid="hero-eyebrow"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: EASE }}
              className="inline-flex items-center gap-2 rounded-full border border-teal-200 bg-teal-50 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.16em] text-teal-800"
            >
              <Truck className="h-3.5 w-3.5" /> Mobile &amp; On-Site · Eastern Iowa
            </motion.span>
            <h1
              data-testid="hero-headline"
              className="mt-6 font-display text-4xl font-bold leading-[1.08] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl"
            >
              {HERO_LINES.map((line, i) => (
                <span key={i} className="block overflow-hidden pb-1">
                  <motion.span
                    className="block"
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: EASE }}
                  >
                    {i === 2 ? (
                      <>
                        on your <span className="text-teal-700">schedule.</span>
                      </>
                    ) : (
                      line
                    )}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p
              data-testid="hero-lead"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6, ease: EASE }}
              className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
            >
              Drug, DNA, DOT, and workplace testing without the waiting room. Certified SwiftTest
              collectors travel to your office, job site, facility, or home — including last-minute
              and after-hours calls across Cedar Rapids, Iowa City, the Quad Cities, Dubuque, and
              beyond.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.75, ease: EASE }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <UrgentButton testId="hero-need-testing-now-button" className="px-6 py-3.5 text-base" />
              <Link
                data-testid="hero-request-testing-link"
                to="/contact"
                className="inline-flex items-center gap-2 rounded-full border-2 border-slate-900 px-6 py-3 text-sm font-bold text-slate-900 transition-all hover:-translate-y-0.5 hover:bg-slate-900 hover:text-white"
              >
                <CalendarCheck className="h-4 w-4" /> Request Scheduled Testing
              </Link>
            </motion.div>
            <motion.ul
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.95 }}
              className="mt-9 flex flex-wrap gap-x-7 gap-y-2.5 text-sm font-semibold text-slate-700"
            >
              {["Certified collectors", "24/7 urgent dispatch", "Lab results in 24–72 hrs"].map((t) => (
                <li key={t} className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-teal-600" /> {t}
                </li>
              ))}
            </motion.ul>
          </div>

          <div className="relative lg:col-span-5">
            <motion.div style={{ y: yImg }} className="relative">
              <div
                className="absolute -inset-3 rotate-2 rounded-[2.2rem] border-2 border-teal-200"
                aria-hidden="true"
              />
              <motion.div
                initial={{ clipPath: "inset(100% 0 0 0)" }}
                animate={{ clipPath: "inset(0% 0 0 0)" }}
                transition={{ duration: 1.1, delay: 0.35, ease: EASE }}
                className="relative overflow-hidden rounded-[2rem] shadow-2xl"
              >
                <img
                  data-testid="hero-image"
                  src={IMAGES.hero.url}
                  alt={IMAGES.hero.alt}
                  className="aspect-[4/5] w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 via-transparent to-transparent" />
              </motion.div>
              <motion.div
                data-testid="hero-card-response"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 1.0, ease: EASE }}
                className="absolute -left-4 top-8 flex items-center gap-3 rounded-2xl border border-slate-100 bg-white/95 px-4 py-3 shadow-xl backdrop-blur sm:-left-8"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
                  <Clock className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-sm font-bold text-slate-900">24/7 Rapid Response</span>
                  <span className="block text-xs text-slate-500">Last-minute &amp; after-hours</span>
                </span>
              </motion.div>
              <motion.div
                data-testid="hero-card-custody"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 1.15, ease: EASE }}
                className="absolute -bottom-5 right-2 flex items-center gap-3 rounded-2xl border border-slate-100 bg-white/95 px-4 py-3 shadow-xl backdrop-blur sm:-right-4"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-teal-100 text-teal-700">
                  <ShieldCheck className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-sm font-bold text-slate-900">Chain-of-Custody</span>
                  <span className="block text-xs text-slate-500">Documented &amp; lab certified</span>
                </span>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <CityMarquee />

      {/* TRUST BADGES */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-5 py-14 sm:px-8">
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6">
            {TRUST_BADGES.map((b, i) => (
              <Reveal key={b.label} delay={i * 0.06}>
                <span
                  data-testid={`trust-badge-${i}`}
                  className="flex items-center gap-2.5 text-sm font-bold text-slate-700"
                >
                  <b.icon className="h-5 w-5 text-teal-700" /> {b.label}
                </span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-24">
          <Reveal className="max-w-2xl">
            <Eyebrow>What we do</Eyebrow>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Every test your workplace needs, delivered on-site
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
              Eight core services, one simple promise: we bring certified, confidential testing to
              you — wherever you are in Eastern Iowa.
            </p>
          </Reveal>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={(i % 4) * 0.07} className={i === 0 || i === 5 ? "lg:col-span-2" : ""}>
                <Link
                  data-testid={`home-service-card-${s.id}`}
                  to={`/services#${s.id}`}
                  className="group flex h-full flex-col rounded-3xl border border-slate-200 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:border-teal-300 hover:shadow-xl"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-50 text-teal-700 transition-colors group-hover:bg-teal-700 group-hover:text-white">
                    <s.icon className="h-6 w-6" />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-bold text-slate-900">{s.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{s.short}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-teal-700">
                    Learn more
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WE COME TO YOU */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-24">
          <div className="grid items-center gap-12 overflow-hidden rounded-[2.5rem] bg-slate-900 p-8 sm:p-14 lg:grid-cols-2">
            <Reveal>
              <Eyebrow>
                <span className="text-teal-300">The mobile difference</span>
              </Eyebrow>
              <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                No clinics. No travel time. No downtime.
              </h2>
              <p className="mt-5 text-base leading-relaxed text-slate-300 sm:text-lg">
                Traditional testing pulls your people off the job for hours. SwiftTest brings the
                entire collection process to your door — offices, manufacturing floors, construction
                sites, and homes.
              </p>
              <ul className="mt-7 space-y-3.5">
                {[
                  "Employees back to work in minutes, not hours",
                  "Same-day, last-minute & after-hours availability",
                  "One call covers one person or your whole crew",
                  "Fully documented, confidential, lab-certified results",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm font-medium text-slate-200">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-teal-400" /> {t}
                  </li>
                ))}
              </ul>
              <Link
                data-testid="home-mobile-testing-link"
                to="/mobile-testing"
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-teal-500 px-6 py-3 text-sm font-bold text-slate-900 transition-all hover:-translate-y-0.5 hover:bg-teal-400"
              >
                How mobile testing works <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="relative">
                <img
                  data-testid="home-mobile-image"
                  src={IMAGES.mobile.url}
                  alt={IMAGES.mobile.alt}
                  className="aspect-[4/3] w-full rounded-3xl object-cover shadow-2xl"
                />
                <div className="absolute -bottom-4 -right-3 rotate-2 rounded-2xl bg-teal-400 px-5 py-3 font-display text-sm font-bold text-slate-900 shadow-xl">
                  We travel ~100 miles
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* AREA CHECKER */}
      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-24">
          <div className="grid items-start gap-12 lg:grid-cols-2">
            <Reveal>
              <Eyebrow>Service area</Eyebrow>
              <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                From Cedar Rapids to the Quad Cities — and everywhere between
              </h2>
              <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
                Our mobile units cover Cedar Rapids, Iowa City, Marion, Hiawatha, Davenport &amp;
                the Quad Cities, Dubuque, and communities within roughly a 100-mile radius. Not
                sure? Check your town in seconds.
              </p>
              <Link
                data-testid="home-service-area-link"
                to="/service-area"
                className="mt-7 inline-flex items-center gap-2 text-sm font-bold text-teal-700 transition-colors hover:text-teal-900"
              >
                Explore the full service area <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
            <Reveal delay={0.15}>
              <AreaChecker />
            </Reveal>
          </div>
        </div>
      </section>

      {/* QUOTE ESTIMATOR */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-24">
          <Reveal className="mx-auto max-w-2xl text-center">
            <span className="inline-flex items-center justify-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-teal-700">
              <span className="h-px w-8 bg-teal-600/60" />
              Instant ballpark
              <span className="h-px w-8 bg-teal-600/60" />
            </span>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Know the cost before you call
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
              Pick a service, headcount, and city — get a working price range in seconds.
            </p>
          </Reveal>
          <Reveal delay={0.12} className="mt-12">
            <QuoteEstimator />
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-5 pb-24 pt-4 sm:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2.5rem] bg-teal-800 px-8 py-14 text-center sm:px-14">
              <div
                className="pointer-events-none absolute -right-20 -top-20 h-72 w-72 rounded-full bg-teal-600/50 blur-3xl"
                aria-hidden="true"
              />
              <h2 className="relative font-display text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Testing on your terms starts with one call
              </h2>
              <p className="relative mx-auto mt-4 max-w-xl text-base text-teal-100 sm:text-lg">
                Urgent situation? We dispatch 24/7. Planning ahead? Send a request and we will
                confirm a time that suits your schedule.
              </p>
              <div className="relative mt-8 flex flex-wrap items-center justify-center gap-4">
                <UrgentButton testId="home-cta-need-testing-now-button" className="px-6 py-3.5 text-base" />
                <a
                  data-testid="home-cta-call-link"
                  href={`tel:${SITE.phoneTel}`}
                  className="inline-flex items-center gap-2 rounded-full bg-white/10 px-6 py-3.5 text-sm font-bold text-white ring-1 ring-white/30 backdrop-blur transition-all hover:-translate-y-0.5 hover:bg-white/20"
                >
                  <Phone className="h-4 w-4" /> {SITE.phoneDisplay}
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
